import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createCompetition, getCompetitionById, updateCompetition } from '../../services/competitionService';
import { getSportsCategories, getAgeGroups } from '../../services/competitionService';
import { Competition, SportsCategory, AgeGroup } from '../../types/models';
import { ArrowLeft, Save } from 'lucide-react';

const CompetitionForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const { t } = useTranslation('common');
  
  const [formData, setFormData] = useState<Omit<Competition, 'event_id' | 'created_at' | 'category' | 'status'>>({
    event_name: '',
    category_id: '',
    gender_category: '',
    is_team_event: false,
    age_group_id: '',
    points_allocation: {
      "1": 5,
      "2": 3,
      "3": 2,
      "participation": 1
    }
  });
  
  const [categories, setCategories] = useState<SportsCategory[]>([]);
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesData, ageGroupsData] = await Promise.all([
          getSportsCategories(),
          getAgeGroups()
        ]);
        setCategories(categoriesData);
        setAgeGroups(ageGroupsData);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load required data');
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchCompetition = async () => {
      if (id) {
        try {
          setLoading(true);
          setError(null);
          const data = await getCompetitionById(id);
          if (data) {
            setFormData({
              event_name: data.event_name,
              category_id: data.category_id,
              gender_category: data.gender_category,
              is_team_event: data.is_team_event,
              age_group_id: data.age_group_id || '',
              points_allocation: data.points_allocation
            });
          }
        } catch (error: any) {
          console.error('Error fetching competition:', error);
          setError(error.message || 'Failed to load competition data');
          if (error.message === 'Competition not found') {
            navigate('/competitions');
          }
        } finally {
          setLoading(false);
        }
      }
    };

    if (isEditMode) {
      fetchCompetition();
    }
  }, [id, isEditMode, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      setFormData(prev => ({ ...prev, [name]: checkbox.checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handlePointsChange = (position: string, value: string) => {
    const points = parseInt(value) || 0;
    setFormData(prev => ({
      ...prev,
      points_allocation: {
        ...prev.points_allocation,
        [position]: points
      }
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      if (isEditMode && id) {
        await updateCompetition(id, formData);
      } else {
        await createCompetition(formData);
      }
      
      navigate('/competitions');
    } catch (error: any) {
      console.error('Error saving competition:', error);
      setError(error.message || 'Failed to save competition');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center">
        <button
          onClick={() => navigate(-1)}
          className="mr-4 inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? t('competitions.form.editTitle') : t('competitions.form.addTitle')}
        </h1>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label htmlFor="event_name" className="block text-sm font-medium text-gray-700">
                {t('competitions.form.fields.eventName.label')}
              </label>
              <input
                type="text"
                name="event_name"
                id="event_name"
                required
                value={formData.event_name}
                onChange={handleChange}
                placeholder={t('competitions.form.fields.eventName.placeholder')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="category_id" className="block text-sm font-medium text-gray-700">
                {t('competitions.form.fields.category.label')}
              </label>
              <select
                id="category_id"
                name="category_id"
                required
                value={formData.category_id}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">{t('competitions.form.fields.category.placeholder')}</option>
                {categories.map((category) => (
                  <option key={category.category_id} value={category.category_id}>
                    {category.category_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  name="is_team_event"
                  id="is_team_event"
                  checked={formData.is_team_event}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_team_event" className="ml-2 block text-sm text-gray-900">
                  {t('competitions.form.fields.isTeamEvent.label')}
                </label>
              </div>
            </div>

            <div>
              <label htmlFor="age_group_id" className="block text-sm font-medium text-gray-700">
                {t('competitions.form.fields.ageGroup.label')}
              </label>
              <select
                id="age_group_id"
                name="age_group_id"
                required
                value={formData.age_group_id}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">{t('competitions.form.fields.ageGroup.placeholder')}</option>
                {formData.is_team_event && (
                  <option value="all">All Age Groups (Team Events Only)</option>
                )}
                {ageGroups.map((group) => (
                  <option key={group.age_group_id} value={group.age_group_id}>
                    {group.group_name} (Birth Years: {group.birth_years.join(', ')})
                  </option>
                ))}
              </select>
              {formData.is_team_event && formData.age_group_id === 'all' && (
                <p className="mt-2 text-sm text-blue-600">
                  This team event will include participants from all age groups.
                </p>
              )}
            </div>

            <div>
              <label htmlFor="gender_category" className="block text-sm font-medium text-gray-700">
                {t('competitions.form.fields.gender.label')}
              </label>
              <select
                id="gender_category"
                name="gender_category"
                required
                value={formData.gender_category}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">{t('competitions.form.fields.gender.placeholder')}</option>
                <option value="M">{t('competitions.filters.male')}</option>
                <option value="F">{t('competitions.filters.female')}</option>
                <option value="A">{t('competitions.filters.all')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('competitions.form.fields.pointsAllocation.label')}
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="points_1" className="block text-sm text-gray-600">
                    {t('competitions.form.fields.pointsAllocation.firstPlace')}
                  </label>
                  <input
                    type="number"
                    id="points_1"
                    value={formData.points_allocation["1"]}
                    onChange={(e) => handlePointsChange("1", e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="points_2" className="block text-sm text-gray-600">
                    {t('competitions.form.fields.pointsAllocation.secondPlace')}
                  </label>
                  <input
                    type="number"
                    id="points_2"
                    value={formData.points_allocation["2"]}
                    onChange={(e) => handlePointsChange("2", e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="points_3" className="block text-sm text-gray-600">
                    {t('competitions.form.fields.pointsAllocation.thirdPlace')}
                  </label>
                  <input
                    type="number"
                    id="points_3"
                    value={formData.points_allocation["3"]}
                    onChange={(e) => handlePointsChange("3", e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="points_participation" className="block text-sm text-gray-600">
                    {t('competitions.form.fields.pointsAllocation.participation')}
                  </label>
                  <input
                    type="number"
                    id="points_participation"
                    value={formData.points_allocation["participation"]}
                    onChange={(e) => handlePointsChange("participation", e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => navigate('/competitions')}
              className="mr-3 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t('competitions.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? t('competitions.actions.saving') : t('competitions.actions.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompetitionForm;